import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tesoreriaService, {
  ChequeTerceroRechazoPreview,
  ChequeTerceroRechazoRecargo,
  ChequeTerceroRechazoResult,
} from '../../services/tesoreriaService';
import {
  buildRechazoRequest,
  recargoEsValido,
  tieneRecargosIncompletos,
} from '../../utils/chequeTerceroRechazo';
import ChequeTerceroRechazoArticuloField from './ChequeTerceroRechazoArticuloField';
import ChequeTerceroRechazoDocumentosSection from './ChequeTerceroRechazoDocumentosSection';
import ChequeTerceroRechazoRecargoRow from './ChequeTerceroRechazoRecargoRow';

interface ChequeTerceroRechazoDialogProps {
  open: boolean;
  nroCarga: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

type FaseRechazo = 'preview' | 'completado';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value ?? 0);

const ChequeTerceroRechazoDialog: React.FC<ChequeTerceroRechazoDialogProps> = ({
  open,
  nroCarga,
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<ChequeTerceroRechazoPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recargos, setRecargos] = useState<ChequeTerceroRechazoRecargo[]>([]);
  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [descripcionArticulo, setDescripcionArticulo] = useState('');
  const [generarNdProveedor, setGenerarNdProveedor] = useState(true);
  const [caeError, setCaeError] = useState<string | null>(null);
  const [fase, setFase] = useState<FaseRechazo>('preview');
  const [resultadoRechazo, setResultadoRechazo] = useState<ChequeTerceroRechazoResult | null>(null);
  const previewRequestIdRef = useRef(0);

  const finalizar = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  const handleDialogClose = () => {
    if (confirming) return;
    if (fase === 'completado') {
      finalizar();
      return;
    }
    onClose();
  };

  const cargarPreview = useCallback(
    async (
      recargosActuales?: ChequeTerceroRechazoRecargo[],
      codigo?: string,
      conservarSiFalla?: boolean
    ) => {
      if (!nroCarga) return;
      const requestId = ++previewRequestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const validos = (recargosActuales ?? []).filter(recargoEsValido);
        const codigoTrim = codigo?.trim();
        const data = await tesoreriaService.previewRechazoChequeTercero(nroCarga, {
          recargos: validos.length > 0 ? validos : undefined,
          codigoArticulo: codigoTrim || undefined,
        });
        if (requestId !== previewRequestIdRef.current) return;
        setPreview(data);
        if (!codigoTrim && data.codigoArticulo) {
          setCodigoArticulo((actual) => (actual.trim() ? actual : data.codigoArticulo));
          setDescripcionArticulo((actual) =>
            actual.trim() ? actual : data.descripcionArticulo || ''
          );
        } else if (
          codigoTrim &&
          data.codigoArticulo.trim().toUpperCase() === codigoTrim.toUpperCase()
        ) {
          setCodigoArticulo(data.codigoArticulo);
          setDescripcionArticulo(data.descripcionArticulo || '');
        }
        if (data.ofrecerNdProveedor) {
          setGenerarNdProveedor(true);
        }
      } catch (e: any) {
        if (requestId !== previewRequestIdRef.current) return;
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.mensaje ||
          e?.message ||
          'No se pudo previsualizar el rechazo.';
        if (conservarSiFalla) {
          enqueueSnackbar(String(msg), { variant: 'error' });
        } else {
          setError(String(msg));
          setPreview(null);
        }
      } finally {
        if (requestId === previewRequestIdRef.current) setLoading(false);
      }
    },
    [nroCarga, enqueueSnackbar]
  );

  useEffect(() => {
    if (open && nroCarga) {
      setFase('preview');
      setResultadoRechazo(null);
      setRecargos([]);
      setCodigoArticulo('');
      setDescripcionArticulo('');
      setCaeError(null);
      setGenerarNdProveedor(true);
      void cargarPreview([]);
    } else if (!open) {
      setFase('preview');
      setResultadoRechazo(null);
      setPreview(null);
      setError(null);
      setCaeError(null);
    }
  }, [open, nroCarga, cargarPreview]);

  const handleRecalcular = () => {
    if (tieneRecargosIncompletos(recargos)) {
      enqueueSnackbar('Completá artículo e importe en todos los recargos antes de recalcular.', {
        variant: 'warning',
      });
      return;
    }
    void cargarPreview(recargos.filter(recargoEsValido), codigoArticulo, true);
  };

  const confirmar = async (opts?: { guardarSinCae?: boolean }) => {
    if (!nroCarga || !preview) return;
    setConfirming(true);
    setCaeError(null);
    try {
      const validos = recargos.filter(recargoEsValido);
      const { status, data } = await tesoreriaService.rechazarChequeTercero(
        nroCarga,
        buildRechazoRequest(validos, {
          generarNdProveedor: preview.ofrecerNdProveedor ? generarNdProveedor : false,
          guardarSinCae: opts?.guardarSinCae,
          idCompania: preview.idCompania,
          codigoArticulo: codigoArticulo.trim() || preview.codigoArticulo,
        })
      );

      if (status === 422 && data?.requiereCae && !data.ndClienteGrabada) {
        setCaeError(data.caeError || data.mensaje || 'No se pudo obtener CAE de AFIP.');
        return;
      }

      if (!data?.exitoso) {
        enqueueSnackbar(data?.mensaje || 'No se pudo rechazar el cheque.', { variant: 'error' });
        return;
      }

      setResultadoRechazo(data);
      setFase('completado');
    } catch (e: any) {
      const msg =
        e?.response?.data?.mensaje ||
        e?.response?.data?.message ||
        e?.message ||
        'Error al rechazar el cheque.';
      enqueueSnackbar(String(msg), { variant: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {fase === 'completado' ? 'Rechazo completado' : 'Rechazar cheque de tercero'}
      </DialogTitle>
      <DialogContent dividers>
        {fase === 'completado' && nroCarga && (
          <Stack spacing={2}>
            <Alert severity="success">
              {resultadoRechazo?.mensaje || 'El cheque fue marcado como rechazado correctamente.'}
              {resultadoRechazo?.cae && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  CAE ND cliente: <strong>{resultadoRechazo.cae}</strong>
                </Typography>
              )}
            </Alert>
            <ChequeTerceroRechazoDocumentosSection
              nroCarga={nroCarga}
              reloadToken={1}
              showDivider={false}
            />
          </Stack>
        )}

        {fase === 'preview' && loading && !preview && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {fase === 'preview' && !loading && error && !preview && <Alert severity="error">{error}</Alert>}

        {fase === 'preview' && preview && (
          <Stack spacing={2}>
            {preview.advertenciaSucVenta && (
              <Alert severity="warning">{preview.advertenciaSucVenta}</Alert>
            )}
            {preview.advertenciaCae && <Alert severity="warning">{preview.advertenciaCae}</Alert>}
            {caeError && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => void confirmar({ guardarSinCae: true })}>
                    Guardar sin CAE
                  </Button>
                }
              >
                {caeError}. Podés guardar la nota de débito sin CAE y emitirla después desde Ventas.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Cheque
                </Typography>
                <Typography variant="body1">
                  N° {preview.nroCheque} — {preview.librador}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CUIT librador: {preview.nroLibrador?.trim() || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Origen actual
                </Typography>
                <Typography variant="body1">{preview.origenDescripcion}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Importe cheque: {formatCurrency(preview.importeCheque)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body1">
                  {preview.nroCliente} — {preview.nombreCliente || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  ND a emitir
                </Typography>
                <Typography variant="body1">
                  Letra {preview.letra} — Suc. {preview.nroSuc}
                  {preview.requiereCae ? ' (requiere CAE)' : ''}
                </Typography>
              </Grid>
            </Grid>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Artículo de la nota de débito
              </Typography>
              {preview.requiereSeleccionArticulo && !codigoArticulo.trim() && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  No hay un artículo sugerido para el cheque rechazado. Seleccioná cuál usar en la nota de débito.
                </Alert>
              )}
              <ChequeTerceroRechazoArticuloField
                codigoArticulo={codigoArticulo}
                descripcion={descripcionArticulo}
                disabled={loading || confirming}
                onChange={(articulo) => {
                  setCodigoArticulo(articulo.codigoArticulo);
                  setDescripcionArticulo(articulo.descripcion);
                  if (!articulo.codigoArticulo.trim()) return;
                  void cargarPreview(
                    recargos.filter(recargoEsValido),
                    articulo.codigoArticulo,
                    true
                  );
                }}
              />
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Previsualización nota de débito
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Neto</TableCell>
                  <TableCell align="right">IVA</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.requiereSeleccionArticulo && (preview.lineasNdCliente || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        La línea del cheque aparece cuando elijas el artículo.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {(preview.lineasNdCliente || []).map((l, idx) => (
                  <TableRow key={`${l.descripcion}-${idx}`}>
                    <TableCell>{l.descripcion}</TableCell>
                    <TableCell align="right">{formatCurrency(l.importeNeto)}</TableCell>
                    <TableCell align="right">{formatCurrency(l.importeIva)}</TableCell>
                    <TableCell align="right">{formatCurrency(l.importeTotal)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Totales</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(preview.subtotalNdCliente)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(preview.ivaNdCliente)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(preview.totalNdCliente)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Recargos adicionales</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setRecargos((prev) => [
                      ...prev,
                      { codigoArticulo: '', descripcion: '', importe: 0 },
                    ])
                  }
                >
                  Agregar
                </Button>
              </Stack>
              {recargos.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Opcional: elegí un artículo financiero o servicio; se precarga el precio neto de catálogo.
                </Typography>
              )}
              {recargos.map((r, idx) => (
                <ChequeTerceroRechazoRecargoRow
                  key={idx}
                  recargo={r}
                  onChange={(next) =>
                    setRecargos((prev) => prev.map((item, i) => (i === idx ? next : item)))
                  }
                  onRemove={() => setRecargos((prev) => prev.filter((_, i) => i !== idx))}
                />
              ))}
              {recargos.length > 0 && (
                <Button size="small" variant="outlined" onClick={handleRecalcular} disabled={loading}>
                  Recalcular previsualización
                </Button>
              )}
            </Box>

            {preview.debitoBanco && (
              <Alert severity="info">
                Se generará un débito bancario en {preview.debitoBanco.nombreBanco || `banco ${preview.debitoBanco.nroBanco}`}{' '}
                por {formatCurrency(preview.debitoBanco.importe)} con leyenda &quot;{preview.debitoBanco.leyenda}&quot;.
              </Alert>
            )}

            {preview.ofrecerNdProveedor && preview.ndProveedor && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={generarNdProveedor}
                      onChange={(e) => setGenerarNdProveedor(e.target.checked)}
                    />
                  }
                  label={`Generar nota de débito al proveedor ${preview.ndProveedor.nroProveedor} — ${preview.ndProveedor.nombreProveedor || ''}`}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  {preview.ndProveedor.descripcionLinea} — {formatCurrency(preview.ndProveedor.importe)}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {fase === 'completado' ? (
          <Button variant="contained" onClick={finalizar}>
            Cerrar
          </Button>
        ) : (
          <>
            <Button onClick={handleDialogClose} disabled={confirming}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => void confirmar()}
              disabled={
                loading ||
                confirming ||
                !preview ||
                Boolean(error) ||
                Boolean(preview.requiereSeleccionArticulo) ||
                !codigoArticulo.trim()
              }
              startIcon={confirming ? <CircularProgress size={18} color="inherit" /> : null}
            >
              Confirmar rechazo
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChequeTerceroRechazoDialog;
